import { Link } from '@tanstack/react-router';
import { ArrowRight, Check, LockKeyhole, ShieldCheck } from 'lucide-react';
import { useSurfaceAccess } from '@/lib/platform/surfaces';
import { SURFACES } from '@/lib/gql/__generated__/surfaces';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const workStages = [
  { label: 'Client', detail: 'Start with the client record that anchors the filing.' },
  { label: 'Tax-year IRS Rule Package', detail: 'Work from the rule package selected for that tax year.' },
  { label: 'Form 1041', detail: 'Prepare the estate or trust income tax Filing.' },
  { label: 'Source boxes and receipt', detail: 'Trace source boxes through review to the recorded receipt.' },
] as const;

function AccessStatus({ status }: { status: ReturnType<typeof useSurfaceAccess> }) {
  if (status === 'waiting') {
    return <p className="text-sm text-muted-foreground">Checking screen access…</p>;
  }
  if (status === 'unavailable') {
    return <p className="text-sm text-muted-foreground">Screen access is temporarily unavailable.</p>;
  }
  if (status === 'denied') {
    return <p className="text-sm text-muted-foreground">This person has no access to this screen.</p>;
  }
  return null;
}

export function FrontDoorPage() {
  const access = useSurfaceAccess(SURFACES['front-door']);

  return (
    <main className="min-h-screen bg-background text-foreground">
      <div className="mx-auto flex min-h-screen max-w-6xl flex-col px-5 py-6 sm:px-8 lg:px-10">
        <header className="flex items-center justify-between gap-4 border-b border-border pb-5">
          <Link
            to="/"
            aria-label="TrustFile home"
            className="flex items-center gap-2 rounded-md font-heading text-lg font-semibold tracking-tight text-primary outline-none hover:text-primary/80 focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ShieldCheck className="size-6" aria-hidden="true" />
            TrustFile
          </Link>
          <nav aria-label="Account access" className="flex items-center gap-2">
            <Button render={<Link to="/sign-in" />} variant="ghost" size="sm" data-jr-key="landing-sign-in">
              Sign in
            </Button>
            <Button render={<Link to="/sign-up" />} size="sm" data-jr-key="landing-sign-up">
              Create access
            </Button>
          </nav>
        </header>

        <div className="flex flex-1 flex-col">
          <section
            aria-labelledby="welcome-heading"
            className="grid gap-10 py-16 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:py-24"
          >
            <div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                Estate and trust income tax Filing
              </p>
              <h1
                id="welcome-heading"
                className="max-w-3xl font-heading text-4xl font-semibold leading-tight tracking-tight sm:text-5xl"
              >
                A secure, exact path from trust records to IRS receipt.
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-muted-foreground">
                TrustFile gives CPA firms a clear place to prepare, validate, approve, transmit, and trace estate and
                trust income tax Filings.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button render={<Link to="/sign-up" />} size="lg" data-jr-key="landing-sign-up-primary">
                  Create secure access
                  <ArrowRight aria-hidden="true" />
                </Button>
                <Button
                  render={<Link to="/sign-in" />}
                  variant="outline"
                  size="lg"
                  data-jr-key="landing-sign-in-secondary"
                >
                  Sign in to TrustFile
                </Button>
              </div>
              <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
                <LockKeyhole className="size-4 text-primary" aria-hidden="true" />
                <span>Built for confidential CPA work.</span>
              </div>
            </div>

            <Card className="border-primary/20 bg-primary/[0.04] shadow-none">
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-[0.16em] text-primary">The filing path</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="divide-y divide-border">
                  {workStages.map((stage, index) => (
                    <li key={stage.label} className="flex gap-4 py-4 first:pt-1 last:pb-1">
                      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-foreground">
                        {index + 1}
                      </span>
                      <div>
                        <h2 className="font-heading font-medium">{stage.label}</h2>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">{stage.detail}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          </section>

          <section aria-labelledby="security-heading" className="border-t border-border py-14 lg:py-18">
            <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
              <div>
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                  Security and confidentiality
                </p>
                <h2 id="security-heading" className="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
                  Every filing step should be easy to trace.
                </h2>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Card className="shadow-none">
                  <CardContent className="pt-5">
                    <Check className="size-5 text-primary" aria-hidden="true" />
                    <h3 className="mt-4 font-heading font-medium">Confidential by design</h3>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      Keep client, trust, and source information within the firm’s filing workspace.
                    </p>
                  </CardContent>
                </Card>
                <Card className="shadow-none">
                  <CardContent className="pt-5">
                    <Check className="size-5 text-primary" aria-hidden="true" />
                    <h3 className="mt-4 font-heading font-medium">Traceable work</h3>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      Move from source boxes to calculated Form 1041 figures, approval, transmission, and receipt.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>
        </div>

        <footer className="flex flex-col gap-3 border-t border-border pt-5 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>TrustFile · Estate and trust income tax Filing workspace</p>
          <AccessStatus status={access} />
        </footer>
      </div>
    </main>
  );
}
